/**
 * StandalonePaymentLinks Component
 *
 * Admin interface for generating standalone payment links.
 * Links can be copied and shared via WhatsApp, email, etc.
 */

import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Link2,
  Copy,
  Check,
  RefreshCw,
  XCircle,
  AlertCircle,
  Loader2,
  ChevronDown,
  User,
  DollarSign,
  Calendar,
  MapPin,
  FileText,
  Clock,
  CheckCircle,
  Ban
} from 'lucide-react';
import { generateLink, getLinks, cancelLink } from '../services/standalonePaymentApi';

// All presential courses with prices
const PRESENTIAL_COURSES = [
  { id: 101, name: 'Bastón y Esposas', price: 30000 },
  { id: 102, name: 'Defensa Personal y Técnicas de Reducción', price: 70000 },
  { id: 103, name: 'Uso y Manejo de Extintores', price: 30000 },
  { id: 104, name: 'Perfeccionamiento Guardia de Seguridad', price: 90000 },
  { id: 105, name: 'Especialización Guardia de Seguridad', price: 95000 },
  { id: 106, name: 'Electricidad Básica e Instalación Domiciliaria', price: 75000 },
  { id: 107, name: 'Formación Guardia de Seguridad Privada', price: 110000 },
];

const STATUS_CONFIG = {
  active:     { label: 'Activo',     color: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30', icon: Clock },
  registered: { label: 'Registrado', color: 'bg-blue-500/20 text-blue-400 border-blue-500/30',       icon: User },
  paid:       { label: 'Pagado',     color: 'bg-green-500/20 text-green-300 border-green-500/30',     icon: CheckCircle },
  expired:    { label: 'Expirado',   color: 'bg-slate-500/20 text-slate-400 border-slate-500/30',     icon: Clock },
  cancelled:  { label: 'Cancelado',  color: 'bg-red-500/20 text-red-400 border-red-500/30',           icon: Ban },
};

function formatCLP(amount) {
  return '$' + Number(amount).toLocaleString('es-CL');
}

function formatDate(dateString) {
  if (!dateString) return '';
  return new Date(dateString).toLocaleDateString('es-CL', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  });
}

export default function StandalonePaymentLinks({ className = '' }) {
  // Auth token from localStorage
  const authToken = localStorage.getItem('admin_token');

  // Generator state
  const [selectedCourseId, setSelectedCourseId] = useState('');
  const [price, setPrice] = useState('');
  const [courseDate, setCourseDate] = useState('');
  const [courseLocation, setCourseLocation] = useState('');
  const [adminNote, setAdminNote] = useState('');
  const [generating, setGenerating] = useState(false);
  const [generatedUrl, setGeneratedUrl] = useState('');
  const [copied, setCopied] = useState(false);
  const [genError, setGenError] = useState('');

  // List state
  const [links, setLinks] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [listError, setListError] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [cancellingId, setCancellingId] = useState(null);
  const [copiedLinkId, setCopiedLinkId] = useState(null);

  // Handle course selection -> auto-fill price
  const handleCourseChange = (e) => {
    const courseId = e.target.value;
    setSelectedCourseId(courseId);
    setGeneratedUrl('');
    setGenError('');

    if (courseId) {
      const course = PRESENTIAL_COURSES.find(c => c.id === parseInt(courseId, 10));
      if (course) setPrice(course.price.toString());
    } else {
      setPrice('');
    }
  };

  // Fetch links
  const fetchLinks = useCallback(async () => {
    setLoading(true);
    setListError('');
    try {
      const filters = { limit: 50 };
      if (statusFilter) filters.paymentStatus = statusFilter;

      const result = await getLinks(filters, authToken);
      setLinks(result.links || []);
      setTotal(result.total || 0);
    } catch (err) {
      console.error('Failed to fetch links:', err);
      setListError(err.message || 'Error al cargar los enlaces');
    } finally {
      setLoading(false);
    }
  }, [authToken, statusFilter]);

  useEffect(() => {
    fetchLinks();
  }, [fetchLinks]);

  // Generate link
  const handleGenerate = async () => {
    if (!selectedCourseId || !price) return;
    setGenerating(true);
    setGenError('');
    setGeneratedUrl('');

    const course = PRESENTIAL_COURSES.find(c => c.id === parseInt(selectedCourseId, 10));
    if (!course) {
      setGenerating(false);
      return;
    }

    try {
      const result = await generateLink({
        courseId: course.id,
        courseName: course.name,
        coursePrice: course.price,
        customPrice: parseInt(price, 10) !== course.price ? parseInt(price, 10) : null,
        courseDate: courseDate || null,
        courseLocation: courseLocation || null,
        adminNote: adminNote || null,
      }, authToken);

      setGeneratedUrl(result.link.paymentUrl);
      fetchLinks();
    } catch (err) {
      console.error('Failed to generate link:', err);
      setGenError(err.message || 'Error al generar el enlace');
    } finally {
      setGenerating(false);
    }
  };

  // Copy to clipboard
  const handleCopy = async (url, linkId = null) => {
    try {
      await navigator.clipboard.writeText(url);
      if (linkId) {
        setCopiedLinkId(linkId);
        setTimeout(() => setCopiedLinkId(null), 2000);
      } else {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }
    } catch {
      // Fallback
      const textarea = document.createElement('textarea');
      textarea.value = url;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      if (linkId) {
        setCopiedLinkId(linkId);
        setTimeout(() => setCopiedLinkId(null), 2000);
      } else {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }
    }
  };

  // Cancel link
  const handleCancel = async (id) => {
    setCancellingId(id);
    try {
      await cancelLink(id, authToken);
      fetchLinks();
    } catch (err) {
      console.error('Failed to cancel link:', err);
      setListError(err.message || 'Error al cancelar');
    } finally {
      setCancellingId(null);
    }
  };

  return (
    <div className={className}>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h2 className="text-2xl font-bold text-white flex items-center gap-2">
            <Link2 className="w-6 h-6 text-cyan-400" />
            Generador de Enlaces de Pago
          </h2>
          <p className="text-white/60 text-sm mt-1">
            Genera enlaces de pago individuales para compartir por WhatsApp o email
          </p>
        </div>
        <motion.button
          onClick={fetchLinks}
          disabled={loading}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          className="px-4 py-2 bg-white/10 hover:bg-white/20 text-white/80 rounded-lg
                     border border-white/10 flex items-center gap-2 text-sm font-medium
                     disabled:opacity-50 transition-all"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Actualizar
        </motion.button>
      </div>

      {/* ─── Link Generator Card ─── */}
      <div className="bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10 p-6 mb-8">
        <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <DollarSign className="w-5 h-5 text-emerald-400" />
          Nuevo Enlace
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          {/* Course Select */}
          <div>
            <label className="block text-sm text-white/70 mb-1.5">Curso *</label>
            <div className="relative">
              <select
                value={selectedCourseId}
                onChange={handleCourseChange}
                className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-2.5
                           text-white appearance-none cursor-pointer
                           focus:outline-none focus:border-cyan-400/50 focus:ring-1 focus:ring-cyan-400/30"
              >
                <option value="" className="bg-slate-800">Seleccionar curso...</option>
                {PRESENTIAL_COURSES.map(c => (
                  <option key={c.id} value={c.id} className="bg-slate-800">
                    {c.name}
                  </option>
                ))}
              </select>
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40 pointer-events-none" />
            </div>
          </div>

          {/* Price */}
          <div>
            <label className="block text-sm text-white/70 mb-1.5">Precio (CLP) *</label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40">$</span>
              <input
                type="number"
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                placeholder="0"
                min="1"
                className="w-full bg-white/10 border border-white/20 rounded-lg pl-8 pr-4 py-2.5
                           text-white placeholder-white/30
                           focus:outline-none focus:border-cyan-400/50 focus:ring-1 focus:ring-cyan-400/30"
              />
            </div>
            {selectedCourseId && price && parseInt(price, 10) !== PRESENTIAL_COURSES.find(c => c.id === parseInt(selectedCourseId, 10))?.price && (
              <p className="text-amber-400 text-xs mt-1">Precio personalizado (diferente al catálogo)</p>
            )}
          </div>

          {/* Date (optional) */}
          <div>
            <label className="block text-sm text-white/70 mb-1.5">
              <Calendar className="inline w-3.5 h-3.5 mr-1" />
              Fecha del curso (opcional)
            </label>
            <input
              type="text"
              value={courseDate}
              onChange={(e) => setCourseDate(e.target.value)}
              placeholder="Ej: 15 de Abril, 2026"
              className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-2.5
                         text-white placeholder-white/30
                         focus:outline-none focus:border-cyan-400/50 focus:ring-1 focus:ring-cyan-400/30"
            />
          </div>

          {/* Location (optional) */}
          <div>
            <label className="block text-sm text-white/70 mb-1.5">
              <MapPin className="inline w-3.5 h-3.5 mr-1" />
              Ubicación (opcional)
            </label>
            <input
              type="text"
              value={courseLocation}
              onChange={(e) => setCourseLocation(e.target.value)}
              placeholder="Ej: Sede Valparaíso"
              className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-2.5
                         text-white placeholder-white/30
                         focus:outline-none focus:border-cyan-400/50 focus:ring-1 focus:ring-cyan-400/30"
            />
          </div>
        </div>

        {/* Admin note */}
        <div className="mb-4">
          <label className="block text-sm text-white/70 mb-1.5">
            <FileText className="inline w-3.5 h-3.5 mr-1" />
            Nota interna (opcional)
          </label>
          <input
            type="text"
            value={adminNote}
            onChange={(e) => setAdminNote(e.target.value)}
            placeholder="Ej: Link para Juan Pérez vía WhatsApp"
            className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-2.5
                       text-white placeholder-white/30
                       focus:outline-none focus:border-cyan-400/50 focus:ring-1 focus:ring-cyan-400/30"
          />
        </div>

        {/* Generate button */}
        <motion.button
          onClick={handleGenerate}
          disabled={!selectedCourseId || !price || generating}
          whileHover={{ scale: 1.01 }}
          whileTap={{ scale: 0.99 }}
          className="w-full py-3 bg-gradient-to-r from-cyan-500 to-blue-500
                     hover:from-cyan-600 hover:to-blue-600
                     text-white font-semibold rounded-xl shadow-lg shadow-cyan-500/20
                     disabled:opacity-50 disabled:cursor-not-allowed
                     flex items-center justify-center gap-2 transition-all"
        >
          {generating ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              Generando...
            </>
          ) : (
            <>
              <Link2 className="w-5 h-5" />
              Generar Enlace de Pago
            </>
          )}
        </motion.button>

        {/* Error */}
        {genError && (
          <div className="mt-3 p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 text-sm flex items-center gap-2">
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            {genError}
          </div>
        )}

        {/* Generated URL */}
        <AnimatePresence>
          {generatedUrl && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="mt-4 p-4 bg-emerald-500/10 border border-emerald-500/20 rounded-xl"
            >
              <p className="text-emerald-400 text-sm font-medium mb-2">Enlace generado (válido por 24 horas):</p>
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={generatedUrl}
                  readOnly
                  className="flex-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2
                             text-white text-sm font-mono truncate"
                />
                <motion.button
                  onClick={() => handleCopy(generatedUrl)}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className={`px-4 py-2 rounded-lg flex items-center gap-1.5 text-sm font-medium transition-all ${
                    copied
                      ? 'bg-emerald-500 text-white'
                      : 'bg-white/10 hover:bg-white/20 text-white border border-white/20'
                  }`}
                >
                  {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  {copied ? 'Copiado' : 'Copiar'}
                </motion.button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* ─── Links Table ─── */}
      <div className="bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10 p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
          <h3 className="text-lg font-semibold text-white">
            Enlaces Generados ({total})
          </h3>

          {/* Status filter */}
          <div className="relative">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="bg-white/10 border border-white/20 rounded-lg px-3 py-1.5
                         text-white text-sm appearance-none cursor-pointer pr-8
                         focus:outline-none focus:border-cyan-400/50"
            >
              <option value="" className="bg-slate-800">Todos</option>
              <option value="active" className="bg-slate-800">Activos</option>
              <option value="registered" className="bg-slate-800">Registrados</option>
              <option value="paid" className="bg-slate-800">Pagados</option>
              <option value="expired" className="bg-slate-800">Expirados</option>
              <option value="cancelled" className="bg-slate-800">Cancelados</option>
            </select>
            <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-white/40 pointer-events-none" />
          </div>
        </div>

        {listError && (
          <div className="mb-4 p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 text-sm flex items-center gap-2">
            <AlertCircle className="w-4 h-4" />
            {listError}
          </div>
        )}

        {loading ? (
          <div className="text-center py-8">
            <Loader2 className="w-8 h-8 text-cyan-400 animate-spin mx-auto mb-2" />
            <p className="text-white/50 text-sm">Cargando enlaces...</p>
          </div>
        ) : links.length === 0 ? (
          <div className="text-center py-8">
            <Link2 className="w-10 h-10 text-white/20 mx-auto mb-2" />
            <p className="text-white/40 text-sm">No hay enlaces generados</p>
          </div>
        ) : (
          <div className="space-y-3">
            {links.map((link) => {
              const statusConf = STATUS_CONFIG[link.paymentStatus] || STATUS_CONFIG.active;
              const StatusIcon = statusConf.icon;
              const effectivePrice = link.customPrice || link.coursePrice;

              return (
                <motion.div
                  key={link.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-white/5 border border-white/10 rounded-xl p-4 hover:border-white/20 transition-all"
                >
                  <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                    {/* Course & Price */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-white font-medium truncate">{link.courseName}</span>
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${statusConf.color}`}>
                          <StatusIcon className="w-3 h-3" />
                          {statusConf.label}
                        </span>
                      </div>
                      <div className="flex flex-wrap items-center gap-3 text-sm text-white/50">
                        <span className="text-emerald-400 font-medium">{formatCLP(effectivePrice)}</span>
                        {link.customPrice && (
                          <span className="text-amber-400 text-xs">(precio personalizado)</span>
                        )}
                        <span>Creado: {formatDate(link.createdAt)}</span>
                        <span>Expira: {formatDate(link.paymentTokenExpiresAt)}</span>
                      </div>
                      {/* Student info if registered */}
                      {link.fullName && (
                        <div className="mt-1 text-sm text-cyan-300 flex items-center gap-1">
                          <User className="w-3.5 h-3.5" />
                          {link.fullName} ({link.email})
                        </div>
                      )}
                      {link.adminNote && (
                        <div className="mt-1 text-xs text-white/30 italic">
                          Nota: {link.adminNote}
                        </div>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {/* Copy button */}
                      {(link.paymentStatus === 'active' || link.paymentStatus === 'registered') && (
                        <motion.button
                          onClick={() => handleCopy(link.paymentUrl, link.id)}
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          className={`px-3 py-1.5 rounded-lg flex items-center gap-1.5 text-xs font-medium transition-all ${
                            copiedLinkId === link.id
                              ? 'bg-emerald-500 text-white'
                              : 'bg-white/10 hover:bg-white/20 text-white/80 border border-white/10'
                          }`}
                        >
                          {copiedLinkId === link.id ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                          {copiedLinkId === link.id ? 'Copiado' : 'Copiar'}
                        </motion.button>
                      )}

                      {/* Cancel button */}
                      {(link.paymentStatus === 'active' || link.paymentStatus === 'registered') && (
                        <motion.button
                          onClick={() => handleCancel(link.id)}
                          disabled={cancellingId === link.id}
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          className="px-3 py-1.5 bg-red-500/10 hover:bg-red-500/20 text-red-400
                                     rounded-lg border border-red-500/20 flex items-center gap-1.5
                                     text-xs font-medium disabled:opacity-50 transition-all"
                        >
                          {cancellingId === link.id ? (
                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                          ) : (
                            <XCircle className="w-3.5 h-3.5" />
                          )}
                          Cancelar
                        </motion.button>
                      )}
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
